import { useEffect, useRef, useState } from 'react';
import { Flex, useDisclosure } from '@chakra-ui/react';
import { useAtom } from 'jotai';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom';
import {
  checkIfCurrentUserIsAttorney,
  checkIfCurrentUserIsOverallAttorney,
  createContract,
  getNegotiationById,
  getNegotiationRoles,
  updateNegotiation
} from 'src/api';
import { navbarColorAtom, userAtom } from 'src/atoms';
import { AlertModal } from 'src/components/core/AlertModal';
import { LoadScreen } from 'src/components/core/LoadScreen';
import { Answer, Negotiation as INegotiation } from 'src/interfaces';
import { NegotiationPanel } from '../components/negotiation/NegotiationPanel';
import { ContractType } from '../interfaces';
import { parseConditions } from 'src/utils/parseConditions';
import { parseAnswers } from 'src/utils/parseAnswers';
import _ from 'lodash';
import { negotiationPanelTheme } from 'src/theme/theme';
export const Negotiation = () => {
  const [negotiation, setNegotiation] = useState<INegotiation | null>();
  const [, setNavbarColor] = useAtom(navbarColorAtom);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    isOpen: isOpenError,
    onClose: onCloseError,
    onOpen: onOpenError
  } = useDisclosure();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isGeneratingContract, setIsGeneratingContract] =
    useState<boolean>(false);

  const [params] = useSearchParams();
  const [isFirstCounter, setIsFirstCounter] = useState<boolean>(false);
  const hasCounterParam = !!params.get('first-counter');
  const roleRetrieved: any = useRef(null);
  const [firstCounterAnswers, setFirstCounterAnswers] = useState<any[]>([]);
  const [isAttorney, setIsAttorney] = useState(false);
  const [isOverallAttorney, setIsOverallAttorney] = useState(false);

  const getAttorneyState = async () => {
    if (negotiation != null) {
      const attorneyResponse = await checkIfCurrentUserIsAttorney(
        negotiation._id
      );
      if (attorneyResponse?.data?.isAttorney == true) {
        setIsAttorney(true);
      } else {
        setIsAttorney(false);
      }
    }
  };

  const getOverallAttorneyState = async () => {
    const attorneyResponse = await checkIfCurrentUserIsOverallAttorney();
    if (attorneyResponse?.data?.isAttorney == true) {
      setIsOverallAttorney(true);
    } else {
      setIsOverallAttorney(false);
    }
  };

  useEffect(() => {
    getAttorneyState();
    getOverallAttorneyState();
  }, [negotiation]);

  const [user] = useAtom(userAtom);
  //Check if this is the first response
  useEffect(() => {
    const setRole = async () => {
      if (
        hasCounterParam &&
        negotiation &&
        negotiation.receiverResponses.length === 0 &&
        roleRetrieved.current === null
      ) {
        const { data } = await getNegotiationRoles(negotiation._id);

        /**
         * Set answers to hidden questions
         */
        const answers = [
          {
            stepId: 'role',
            subStepId: 'role',
            questionId: 'role',
            fieldId: 'role',
            value: data.receiverRole
          }
        ];

        const hiddenQuestions = [];
        const conditions = (negotiation?.contractType as ContractType)
          ?.conditions;
        for (const step of (negotiation?.contractType as ContractType)?.form
          ?.steps) {
          for (const subStep of step?.subSteps) {
            const subStepCondition = conditions.subSteps?.find(
              (c: any) => c[subStep.id] !== undefined
            );
            if (
              subStepCondition != null &&
              !parseConditions(subStepCondition, parseAnswers(answers))
            ) {
              continue;
            }
            for (const question of subStep.questions) {
              const questionCondition = conditions.questions?.find(
                (c: any) => c[question.id] !== undefined
              );
              if (!question.hidden) {
                continue;
              }
              const conditionForBeingHidden =
                question?.pathWithDefaultValue?.condition;
              if (
                conditionForBeingHidden != null &&
                !parseConditions(conditionForBeingHidden, parseAnswers(answers))
              ) {
                continue;
              }
              if (
                questionCondition != null &&
                !parseConditions(questionCondition, parseAnswers(answers))
              ) {
                continue;
              }
              hiddenQuestions.push({
                ...question,
                stepId: step.id,
                subStepId: subStep.id
              });
            }
          }
        }

        for (const hiddenQuestion of hiddenQuestions) {
          const value = _.get(
            { user },
            hiddenQuestion?.pathWithDefaultValue.value
          );
          answers.push({
            stepId: hiddenQuestion.stepId,
            subStepId: hiddenQuestion.subStepId,
            fieldId: hiddenQuestion.field.id,
            questionId: hiddenQuestion.id,
            value
          });
        }

        setNegotiation({
          ...negotiation,
          latestReceiverResponses: {
            date: new Date().toString(),
            answers
          },
          receiverResponses: [
            {
              date: new Date().toString(),
              answers
            }
          ]
        });
        setIsFirstCounter(true);
        setFirstCounterAnswers(answers);
        roleRetrieved.current = data.receiverRole;
      }
    };

    setRole();
  }, [hasCounterParam, negotiation, user]);

  useEffect(() => {
    if (location.state?.showSentAlert) {
      onOpen();
    }
  }, [location.state?.showSentAlert, negotiation, onOpen]);

  useEffect(() => {
    const getNegotiation = async () => {
      const { data } = await getNegotiationById(id!);

      const { initiator, receiver, receiverRole } = data;
      const isUserInitiator = user!.email === initiator.email;

      let newReceiver: any = {};

      if (!isUserInitiator && !receiver) {
        newReceiver = {
          firstName: user!.firstName,
          lastName: user!.lastName,
          email: user!.email,
          role: receiverRole,
          profileImageUrl: ''
        };
        setNegotiation({ ...data, receiver: newReceiver });
      } else {
        setNegotiation({ ...data });
      }
    };

    try {
      setIsLoading(true);
      getNegotiation();
    } catch (error) {
      onOpenError();
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  }, [id, onOpenError, setNegotiation, user]);

  useEffect(() => {
    setNavbarColor(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmitAnswers = async (answers: Answer[]) => {
    let cleanAnswers: Answer[] = answers.map(
      ({ stepId, subStepId, questionId, fieldId, value }) => ({
        stepId,
        subStepId,
        questionId,
        fieldId,
        value
      })
    ); //This is because Luke's API doesn't check that form of the answer object - need to fix that

    if (isFirstCounter) {
      cleanAnswers = [...cleanAnswers, ...firstCounterAnswers];
    }

    try {
      setIsLoading(true);
      const { data } = await updateNegotiation(negotiation!._id, {
        answers: cleanAnswers
      });
      setNegotiation(data);
      if (isOverallAttorney) {
        handleViewContract();
      } else {
        onOpen();
      }
    } catch (err) {
      setIsLoading(false);
      onOpenError();
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewContract = async () => {
    try {
      setIsGeneratingContract(true);
      const { data } = await createContract(negotiation!._id);
      setIsGeneratingContract(false);
      navigate(`/contract/${data.contract._id}`);
    } catch (err) {
      setIsGeneratingContract(false);
      onOpenError();
    } finally {
      setIsGeneratingContract(false);
    }
  };

  if (isGeneratingContract) {
    return <LoadScreen label="Generating contract..." />;
  }

  if (isLoading) {
    return <LoadScreen label="Loading..." />;
  }

  if (!negotiation) {
    return <></>;
  }

  return (
    <Flex
      flexDirection="column"
      h="100vh"
      // bgImage="url(/studio.jpg)"
      // bgPosition="center"
      // bgSize="cover"
      bg={negotiationPanelTheme.background}
      overflowY={{ lg: 'hidden' }}
    >
      <Flex
        h="100%"
        alignItems="center"
        justifyContent="space-between"
        overflowY={{ base: 'auto', lg: 'hidden' }}
      >
        <NegotiationPanel
          negotiation={negotiation}
          isFirstCounter={isFirstCounter}
          onSubmitAnswers={handleSubmitAnswers}
          onViewContract={handleViewContract}
        />
      </Flex>
      <AlertModal
        isOpen={isOpen}
        onAccept={onClose}
        acceptText="Continue"
        title={
          isAttorney
            ? 'Your edits have been submitted'
            : 'Step through has been completed. You can now review your terms and edit them before sending them out for approval.'
        }
        variant="success"
      />
      <AlertModal
        isOpen={isOpenError}
        onAccept={onCloseError}
        variant="warning"
        title="Something went wrong!"
        acceptText="OK"
      />
    </Flex>
  );
};
